import { api } from '../../lib/api';
import { dayKey, endOfDaysFromNow, startOfMonth, startOfToday } from '../../lib/periods';

/**
 * The reads the dashboard makes, defined once so a KPI and the widget below it
 * share a cache entry (and the network request) instead of fetching twice.
 *
 * Keys are deliberately the same shape the rest of the app invalidates —
 * `['inventory', …]`, `['batches', …]`, `['quality', …]`, `['recoveries', …]` —
 * so an action taken from a dashboard link refreshes what the dashboard shows
 * when the person returns to it.
 */

export const stockValuationQuery = {
  queryKey: ['reports', 'stock-valuation', 'dashboard'] as const,
  queryFn: () => api.reports.stockValuation({}),
};

export const lowStockQuery = {
  queryKey: ['inventory', 'low-stock'] as const,
  queryFn: api.inventory.lowStock,
};

export const onHoldQuery = {
  queryKey: ['quality', 'pending'] as const,
  queryFn: api.quality.pending,
};

export const openBatchesQuery = {
  queryKey: ['batches', 'board'] as const,
  queryFn: () => api.batches.list({ limit: 100 }),
};

export function expiringLotsQuery(days: number) {
  return {
    queryKey: ['inventory', 'lots', 'expiring', days, dayKey()] as const,
    queryFn: () => api.inventory.lots({ expiringBefore: endOfDaysFromNow(days), limit: 100 }),
  };
}

export function recentMovementsQuery(limit: number) {
  return {
    queryKey: ['inventory', 'movements', 'recent', limit] as const,
    queryFn: () => api.inventory.movements({ limit }),
  };
}

export function receiptsTodayQuery() {
  return {
    queryKey: ['inventory', 'movements', 'receipts-today', dayKey()] as const,
    queryFn: () => api.inventory.movements({ type: 'receipt', from: startOfToday(), limit: 1 }),
  };
}

export function recoveriesThisMonthQuery() {
  return {
    queryKey: ['reports', 'recovery-credit', 'month', dayKey()] as const,
    queryFn: () => api.reports.recoveryCredit({ from: startOfMonth() }),
  };
}

export const recentRecoveriesQuery = {
  queryKey: ['recoveries', 'recent'] as const,
  queryFn: () => api.recoveries.list({ limit: 6 }),
};

export const recentDecisionsQuery = {
  queryKey: ['quality', 'history', 'recent'] as const,
  queryFn: () => api.quality.list({ limit: 6 }),
};

export const decisionsRecordedQuery = {
  queryKey: ['quality', 'history', 'count'] as const,
  queryFn: () => api.quality.list({ limit: 1 }),
};

export function recentAuditQuery(limit: number) {
  return {
    queryKey: ['audit', 'recent', limit] as const,
    queryFn: () => api.audit.list({ limit }),
  };
}

export const userCountQuery = {
  queryKey: ['users', 'count'] as const,
  queryFn: () => api.users.list({ limit: 1 }),
};

export const roleCountQuery = {
  queryKey: ['roles', 'count'] as const,
  queryFn: () => api.roles.list({}),
};

export const healthQuery = {
  queryKey: ['health'] as const,
  queryFn: api.health,
};

export const readinessQuery = {
  queryKey: ['ready'] as const,
  queryFn: api.readiness,
};
